// Partnership bidding (Paul, 16 Oct 2026; layout modelled on Cuebids).
//   /partner-bidding                your sets, start a set, join with a code
//   /partner-bidding/join/:code     join a partner's set from an invite link
//   /partner-bidding/:setId         the set's boards: your hand, whose turn, result
//   /partner-bidding/:setId/:n      one board: the auction, the bidding box, the result
// Private: results are seen by the two partners only. The datum is the best double-dummy
// result ("Par" since 18 Oct 2026: both sides bidding perfectly, sacrifices included). All wording here is neutral structural labels for Paul to replace.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { pb, testUid } from "./pbApi";
import { TOPIC_LABEL, PAIR_LABEL, sourceLabel } from "./pbLabels";
import "./PartnerBidding.css";

const SYM = { S: "♠", H: "♥", D: "♦", C: "♣", N: "NT" };
const SEAT_NAME = { N: "North", E: "East", S: "South", W: "West" };
const COLS = ["W", "N", "E", "S"];
const LIST_ORDER = ["S", "H", "D", "C"]; // hands written in one line
const FAN_ORDER = ["S", "H", "C", "D"]; // cards laid out: colours alternate
const STRAINS = ["C", "D", "H", "S", "N"];
const VUL_SEATS = { none: [], ns: ["N", "S"], ew: ["E", "W"], both: ["N", "E", "S", "W"] };
// Neutral structural labels - Paul to supply his own wording. Pools come from the function
// ("sources"); a pool without a label here is not offered.
// Labels live in pbLabels.js (shared with Just Play's "Waiting for you").
const SOURCE_LABEL = TOPIC_LABEL;
const OPPONENT_OPTIONS = [
  { id: "pass", label: "Opponents pass" },
  { id: "bot", label: "Robot opponents" },
];
// Page wording (Claude's, at Paul's request 18 Oct 2026: "you come up with wording").
const INTRO =
  "Bid hands with your partner in your own time. You each bid when it's your turn, then compare your auction with par and, on hands from top events, with the real players.";
const rank = (r) => (r === "T" ? "10" : r);
const suitClass = (s) => `pb-s-${s}`;
const isBid = (c) => /^[1-7][CDHSN]$/.test(c);
const bidValue = (c) => (Number(c[0]) - 1) * 5 + STRAINS.indexOf(c[1]);

function Call({ c, small, alert, bot }) {
  const cls = `pb-call ${small ? "pb-call--sm" : ""} ${alert ? "pb-call--alert" : ""} ${bot ? "pb-call--bot" : ""}`;
  if (c === "P") return <span className={`${cls} pb-call--pass`}>P</span>;
  if (c === "X" || c === "XX") return <span className={`${cls} pb-call--dbl`}>{c}</span>;
  return (
    <span className={`${cls} ${suitClass(c[1])}`}>
      {c[0]}
      <span className={c[1] === "N" ? "pb-nt" : ""}>{SYM[c[1]]}</span>
    </span>
  );
}

function ContractChip({ k }) {
  if (!k) return <span className="pb-call pb-call--pass">Pass</span>;
  return (
    <span className="pb-contract">
      <Call c={`${k.level}${k.strain}`} />
      {k.dbl ? <span className="pb-call pb-call--dbl">{k.dbl}</span> : null}
    </span>
  );
}

function Stars({ n }) {
  return (
    <span className="pb-stars" aria-label={`${n} of 3 stars`}>
      {[0, 1, 2].map((i) => (
        <span key={i} className={i < n ? "pb-star pb-star--on" : "pb-star"}>★</span>
      ))}
    </span>
  );
}

function HandLine({ hand }) {
  return (
    <span className="pb-handLine">
      {LIST_ORDER.map((s) => (
        <span key={s} className="pb-handSuit">
          <span className={suitClass(s)}>{SYM[s]}</span>
          {hand[s] ? hand[s].split("").map(rank).join("") : "—"}
        </span>
      ))}
    </span>
  );
}

function Fan({ hand, label }) {
  const cards = FAN_ORDER.flatMap((s) => hand[s].split("").map((r) => ({ s, r })));
  return (
    <div className="pb-fanWrap">
      <div className="pb-fanLabel">{label}</div>
      <div className="pb-fan" role="img" aria-label={`${label}: ${LIST_ORDER.map((s) => `${SYM[s]} ${hand[s] || "void"}`).join(", ")}`}>
        {cards.map((c) => (
          <span key={c.s + c.r} className={`pb-card ${suitClass(c.s)}`}>
            <span className="pb-cardRank">{rank(c.r)}</span>
            <span className="pb-cardSuit">{SYM[c.s]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// auction: call strings, or { c, a, e } objects (a table's auction). notes: per-call { a, e, src }.
// An alerted call is marked; tapping it shows the explanation underneath the grid.
function AuctionGrid({ auction, notes = [], dealer, vul, toAct, names }) {
  const [open, setOpen] = useState(null);
  const items = auction.map((x, i) => {
    const c = typeof x === "string" ? x : x.c;
    const n = typeof x === "string" ? notes[i] : x.a || x.e ? { a: x.a, e: x.e, src: "table" } : null;
    return { c, n, i };
  });
  const start = COLS.indexOf(dealer);
  const cells = [...Array(start).fill(null), ...items];
  if (toAct) cells.push("?");
  const rows = [];
  for (let i = 0; i < cells.length; i += 4) rows.push(cells.slice(i, i + 4));
  const vulSeats = VUL_SEATS[vul] || [];
  const openNote = open !== null && items[open] && items[open].n;
  return (
    <div className="pb-auctionWrap">
      <table className="pb-auction">
        <thead>
          <tr>
            {COLS.map((s) => (
              <th key={s} className={vulSeats.includes(s) ? "pb-vul" : ""}>
                {s === dealer ? <span className="pb-dealer" title="Dealer">{s}</span> : s}
                {names && names[s] ? <span className="pb-seatName">{names[s]}</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {[0, 1, 2, 3].map((k) => {
                const x = r[k];
                if (x === "?") return <td key={k}><span className="pb-call pb-call--q">?</span></td>;
                if (!x) return <td key={k} />;
                const alert = !!(x.n && (x.n.a || x.n.e));
                const bot = !!(x.n && /^bot/.test(x.n.src || ""));
                const call = <Call c={x.c} small alert={alert} bot={bot} />;
                return (
                  <td key={k}>
                    {alert ? (
                      <button type="button" className="pb-callBtn" onClick={() => setOpen(open === x.i ? null : x.i)} aria-expanded={open === x.i} aria-label="Show the explanation">
                        {call}
                      </button>
                    ) : call}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {openNote && (
        <p className="pb-note" role="status">
          <Call c={items[open].c} small /> {openNote.e || "Alerted (no explanation given)"}
        </p>
      )}
    </div>
  );
}

function BidBox({ auction, dealer, onCall, busy }) {
  const last = [...auction].reverse().find(isBid);
  // Double: the last call that wasn't a pass is the opponents' bid. Redouble: it is their double.
  let la = null;
  for (let i = auction.length - 1; i >= 0; i--) {
    if (auction[i] !== "P") {
      la = { c: auction[i], seat: COLS[(COLS.indexOf(dealer) + i) % 4] };
      break;
    }
  }
  const me = COLS[(COLS.indexOf(dealer) + auction.length) % 4];
  const theirs = la && ["N", "S"].includes(la.seat) !== ["N", "S"].includes(me);
  const canX = theirs && isBid(la.c);
  const canXX = theirs && la.c === "X";
  const legal = (c) => !last || bidValue(c) > bidValue(last);
  const firstLevel = last ? Number(last[0]) + (last[1] === "N" ? 1 : 0) : 1;
  const [level, setLevel] = useState(Math.min(firstLevel, 7));
  useEffect(() => setLevel(Math.min(firstLevel, 7)), [firstLevel]);
  return (
    <div className="pb-bidbox">
      <div className="pb-levels" role="tablist" aria-label="Level">
        {[1, 2, 3, 4, 5, 6, 7].map((L) => (
          <button
            key={L}
            type="button"
            role="tab"
            aria-selected={L === level}
            className={`pb-level ${L === level ? "pb-level--on" : ""}`}
            disabled={L < firstLevel || busy}
            onClick={() => setLevel(L)}
          >
            {L}
          </button>
        ))}
      </div>
      <div className="pb-strains">
        {STRAINS.map((s) => {
          const c = `${level}${s}`;
          return (
            <button key={s} type="button" className={`pb-strainBtn pb-sb-${s}`} disabled={!legal(c) || busy} onClick={() => onCall(c)} aria-label={`${level} ${s === "N" ? "no trump" : SYM[s]}`}>
              {level}
              <span className={s === "N" ? "pb-nt" : ""}>{SYM[s]}</span>
            </button>
          );
        })}
        <button type="button" className="pb-passBtn" disabled={busy} onClick={() => onCall("P")}>
          Pass
        </button>
        {(canX || canXX) && (
          <button type="button" className="pb-dblBtn" disabled={busy} onClick={() => onCall(canX ? "X" : "XX")}>
            {canX ? "X" : "XX"}
          </button>
        )}
      </div>
    </div>
  );
}

// After the auction: the real pair's auction, then the other room's, with the players' names.
function TableRooms({ b }) {
  const pairOf = (r, a, c) => [r.names[a], r.names[c]].filter(Boolean).join("–");
  const Room = ({ r, title }) => (
    <section className="pb-room">
      <h3 className="pb-roomTitle">
        {title}: {pairOf(r, "N", "S") || "North–South"}
        <span className="pb-muted"> v {pairOf(r, "E", "W") || "East–West"}</span>
      </h3>
      <AuctionGrid auction={r.auction} dealer={b.dealer} vul={b.vul} names={r.names} />
      <div className="pb-roomResult">
        <ContractChip k={r.dd.contract} />
        <span>{r.dd.contract ? SEAT_NAME[r.dd.contract.declarer] : "Passed out"}</span>
        {r.result ? <span className="pb-muted">Table: {r.result}</span> : null}
      </div>
    </section>
  );
  return (
    <div className="pb-rooms">
      {b.source && (
        <p className="pb-muted pb-sourceLine">
          {b.source.event}
          {b.source.seg ? `, ${b.source.seg}` : ""}, board {b.source.board}
        </p>
      )}
      <p className="pb-vsTable">
        You v {pairOf(b.featured, "N", "S")}: <strong>{b.vsFeatured > 0 ? `+${b.vsFeatured}` : b.vsFeatured} IMPs</strong>
        <span className="pb-muted"> (both scored double-dummy)</span>
      </p>
      <Room r={b.featured} title="Table" />
      {b.other && <Room r={b.other} title="Other room" />}
    </div>
  );
}

function ContractsTable({ dd }) {
  return (
    <table className="pb-dd">
      <caption>Tricks double-dummy</caption>
      <thead>
        <tr>
          <th />
          {["N", "S", "H", "D", "C"].map((s) => (
            <th key={s} className={suitClass(s)}>{SYM[s]}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {["N", "S"].map((p) => (
          <tr key={p}>
            <th>{SEAT_NAME[p]}</th>
            {["N", "S", "H", "D", "C"].map((s) => (
              <td key={s}>{dd[s + p]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const fmtScore = (n) => (n > 0 ? `+${n}` : String(n));

function ResultPanel({ result, dd }) {
  const [showDD, setShowDD] = useState(false);
  const { mine, best } = result;
  return (
    <div className="pb-result">
      <div className="pb-resultRow">
        <div className="pb-resultSide">
          <div className="pb-resultLabel">You</div>
          <ContractChip k={mine.contract} />
          <div className="pb-resultSub">{mine.contract ? SEAT_NAME[mine.contract.declarer] : "Passed out"}</div>
          <div className="pb-resultScore">{fmtScore(mine.score)}</div>
        </div>
        <div className="pb-resultMid">
          <Stars n={result.stars} />
          <div className="pb-imps">{result.imps === 0 ? "Matched par" : `${result.imps > 0 ? "+" : ""}${result.imps} IMPs`}</div>
        </div>
        <div className="pb-resultSide">
          <div className="pb-resultLabel">Par</div>
          <ContractChip k={best.contract} />
          <div className="pb-resultSub">{best.contract ? SEAT_NAME[best.contract.declarer] : "Pass"}</div>
          <div className="pb-resultScore">{fmtScore(best.score)}</div>
        </div>
      </div>
      {dd && (
        <button type="button" className="pb-linkBtn" onClick={() => setShowDD((v) => !v)} aria-expanded={showDD}>
          {showDD ? "Hide contracts" : "Contracts"}
        </button>
      )}
      {showDD && dd && <ContractsTable dd={dd} />}
    </div>
  );
}

function usePoll(fn, ms, active) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => {
      if (!document.hidden) ref.current();
    }, ms);
    return () => clearInterval(t);
  }, [ms, active]);
}

// ---------- Home: your sets ----------
function Home({ history }) {
  const [sets, setSets] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => pb("list").then((r) => setSets(r.sets)).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  usePoll(load, 20000, true);

  // ?boards=slam on the link preselects a set type (e.g. a slam-only invitation).
  const wanted = new URLSearchParams(window.location.search).get("boards");
  const [topic, setTopic] = useState(SOURCE_LABEL[wanted] ? wanted : "slam");
  // Famous pairs (Paul, 20 Oct 2026: "allow user to just choose all not have to click a specific pair").
  const [pairKey, setPairKey] = useState("all");
  const [slamsOnly, setSlamsOnly] = useState(false);
  const source = topic !== "famous" ? topic : slamsOnly ? `pairslam:${pairKey}` : pairKey === "all" ? "pairs:all" : `pair:${pairKey}`;
  const setSource = (id) => setTopic(id);
  const [opponents, setOpponents] = useState("pass");
  const [sources, setSources] = useState(null);
  // Partners from the partner hub: a set goes straight to one of them, no code (Paul, 18 Oct 2026).
  const [partners, setPartners] = useState([]);
  const [partnerUid, setPartnerUid] = useState("");
  const [partnerEmail, setPartnerEmail] = useState("");
  const [upsell, setUpsell] = useState(null);
  useEffect(() => {
    pb("partners")
      .then((r) => {
        setPartners(r.partners || []);
        if (r.partners && r.partners.length) setPartnerUid(r.partners[0].uid);
      })
      .catch(() => {});
  }, []);
  // Private for now: only allowed accounts can start sets; a partner sees the sets they joined.
  const [access, setAccess] = useState(null);
  useEffect(() => {
    pb("access")
      .then((a) => {
        setAccess(a);
        if (a.member === false) setSource("random"); // themes and the bot are for members (Paul, 18 Oct 2026)
      })
      .catch(() => setAccess({ canStart: false, inSets: false }));
  }, []);
  useEffect(() => {
    if (!access || !access.canStart) return;
    pb("sources")
      .then((r) => setSources(r.sources.filter((s) => s.boards > 0)))
      .catch(() => {
        setSources([{ id: "random", size: 12, boards: 1 }]); // an older backend offers random sets only
        setSource("random");
      });
  }, [access]);

  if (!access) return <div className="pb-page"><p className="pb-muted">Loading…</p></div>;
  if (!access.canStart && !access.inSets) {
    return (
      <div className="pb-page">
        <h1 className="pb-title">Partnership bidding</h1>
        <p>This isn't available yet.</p>
      </div>
    );
  }
  const membersOnly = (id) => access.member === false && id !== "random"; // "famous" included
  // Tapping a subscribers-only option shows a Subscribe prompt there instead of doing nothing (Paul, 19 Oct 2026:
  // "I want them to have option to upgrade easily").
  const byId = Object.fromEntries((sources || []).map((x) => [x.id, x]));
  const order = Object.keys(SOURCE_LABEL);
  const sourceOptions = (sources || []).filter((x) => SOURCE_LABEL[x.id]).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  // One "Famous pairs" choice, placed just before Random.
  if (byId["pairs:all"]) sourceOptions.splice(Math.max(0, sourceOptions.findIndex((x) => x.id === "random")), 0, { id: "famous", size: byId["pairs:all"].size, boards: byId["pairs:all"].boards });
  const pairKeys = Object.keys(PAIR_LABEL).filter((k) => byId[`pair:${k}`]);
  const create = async () => {
    setBusy(true);
    setErr("");
    try {
      if (!partnerUid && !partnerEmail.trim()) {
        setErr("Choose your partner, or type their email address.");
        setBusy(false);
        return;
      }
      const r = await pb("create", { source, opponents, ...(partnerUid ? { partnerUid } : { partnerEmail: partnerEmail.trim() }) });
      history.push(`/partner-bidding/${r.id}`);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="pb-page">
      <Link to="/partner" className="pb-back pb-backText">‹ Partners</Link>
      <h1 className="pb-title">Partnership bidding</h1>
      <p className="pb-intro">{INTRO}</p>
      {access.canStart && access.member === false && <SubscribeHook />}
      {access.canStart && <div className="pb-start">
        <div className="pb-field">
          <span className="pb-fieldLabel" id="pb-src">Boards</span>
          <span className="pb-muted pb-fieldHint">Each set is 6 boards picked at random from your choice (Random: 12).</span>
          <span className="pb-muted pb-fieldHint">Random deals with the opponents passing are free. Theme hands and robot opponents need you both to be subscribers.</span>
          <div className="pb-seg" role="radiogroup" aria-labelledby="pb-src">
            {sourceOptions.map((o) => (
              <button key={o.id} type="button" role="radio" aria-checked={topic === o.id} className={`pb-segBtn ${topic === o.id ? "pb-segBtn--on" : ""} ${membersOnly(o.id) ? "pb-segBtn--locked" : ""}`} onClick={() => (membersOnly(o.id) ? setUpsell("source") : (setTopic(o.id), setUpsell(null)))}>
                {o.id === "famous" ? "Famous pairs" : SOURCE_LABEL[o.id]} <span className="pb-muted">· {membersOnly(o.id) ? "Subscribers" : `${o.boards.toLocaleString()} hands`}</span>
              </button>
            ))}
            {!sources && <span className="pb-muted">Loading…</span>}
          </div>
          {upsell === "source" && <UpgradeNote what="Theme hands from top events are" />}
        </div>
        {topic === "famous" && (
          <div className="pb-field">
            <span className="pb-fieldLabel" id="pb-pair">Pair</span>
            <div className="pb-seg" role="radiogroup" aria-labelledby="pb-pair">
              {["all", ...pairKeys].map((k) => (
                <button key={k} type="button" role="radio" aria-checked={pairKey === k} className={`pb-segBtn ${pairKey === k ? "pb-segBtn--on" : ""}`} onClick={() => setPairKey(k)}>
                  {k === "all" ? "All pairs" : PAIR_LABEL[k]}
                </button>
              ))}
            </div>
            <div className="pb-seg" role="radiogroup" aria-label="Which hands">
              {[[false, "All hands"], [true, "Slams only"]].map(([v, label]) => (
                <button key={label} type="button" role="radio" aria-checked={slamsOnly === v} className={`pb-segBtn ${slamsOnly === v ? "pb-segBtn--on" : ""}`} onClick={() => setSlamsOnly(v)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {source === "random" && (
          <div className="pb-field">
            <span className="pb-fieldLabel" id="pb-opp">Opponents</span>
            <div className="pb-seg" role="radiogroup" aria-labelledby="pb-opp">
              {OPPONENT_OPTIONS.map((o) => (
                <button key={o.id} type="button" role="radio" aria-checked={opponents === o.id} className={`pb-segBtn ${opponents === o.id ? "pb-segBtn--on" : ""} ${access.member === false && o.id === "bot" ? "pb-segBtn--locked" : ""}`} onClick={() => (access.member === false && o.id === "bot" ? setUpsell("bot") : (setOpponents(o.id), setUpsell(null)))}>
                  {o.label}
                  {access.member === false && o.id === "bot" ? <span className="pb-muted"> · Subscribers</span> : null}
                </button>
              ))}
            </div>
            {upsell === "bot" && <UpgradeNote what="Robot opponents are" />}
          </div>
        )}
        {/* Choose what to do first, then who with (Paul, 18 Oct 2026). */}
        <div className="pb-field">
          <span className="pb-fieldLabel" id="pb-who">Partner</span>
          <div className="pb-seg" role="radiogroup" aria-labelledby="pb-who">
            {partners.map((p) => (
              <button key={p.uid} type="button" role="radio" aria-checked={partnerUid === p.uid} className={`pb-segBtn ${partnerUid === p.uid ? "pb-segBtn--on" : ""}`} onClick={() => setPartnerUid(p.uid)}>
                {p.name}
              </button>
            ))}
            <button type="button" role="radio" aria-checked={partnerUid === ""} className={`pb-segBtn ${partnerUid === "" ? "pb-segBtn--on" : ""}`} onClick={() => setPartnerUid("")}>
              Invite by email
            </button>
          </div>
          {partnerUid === "" && (
            <div className="pb-emailRow">
              <label htmlFor="pb-email" className="pb-srOnly">Your partner's email address</label>
              <input id="pb-email" className="browser-default" type="email" inputMode="email" autoComplete="off" placeholder="Your partner's email address" value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} />
              <p className="pb-muted pb-fieldHint pb-emailNote">Your partner needs a Bridge Champions account (a free one is fine). Use the email they signed up with.</p>
            </div>
          )}
        </div>
        <button type="button" className="pb-primary" onClick={create} disabled={busy}>Start a new set</button>
      </div>}
      {err && <p className="pb-err" role="alert">{err}</p>}
      {err && /isn't one yet/.test(err) && (
        <p className="pb-muted">They can subscribe at <Link to={SUBSCRIBE_URL}>bridgechampions.com/membership</Link>, or choose Random with the opponents passing, which is free.</p>
      )}
      {sets === null && !err && <p className="pb-muted">Loading…</p>}
      {sets && sets.length === 0 && <p className="pb-muted">No sets yet. Choose the hands and your partner above, then press Start a new set.</p>}
      <ul className="pb-sets">
        {(sets || []).map((s) => (
          <li key={s.id}>
            <Link to={`/partner-bidding/${s.id}`} className="pb-setRow">
              <span className="pb-setWho">
                <span className="pb-pill">{sourceLabel(s.source)}</span>{" "}
                {s.partnerName ? `With ${s.partnerName}` : "Waiting for your partner"}
              </span>
              <span className="pb-setMeta">
                {s.yourTurn > 0 && <span className="pb-pill pb-pill--you">Your turn: {s.yourTurn}</span>}
                {s.partnerTurn > 0 && <span className="pb-pill">Partner's turn: {s.partnerTurn}</span>}
                <span className="pb-muted">{s.done} of {s.size} done</span>
                <span className="pb-starTotal">★ {s.stars}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Shown to non-members: theme sets and robot opponents need both partners subscribed (Paul, 18 Oct 2026).
// Wording is Claude's suggestion at Paul's request ("how would you describe this") - his to change.
const SUBSCRIBE_URL = "/membership?plan=yearly";
function SubscribeHook() {
  return (
    <div className="pb-hook">
      <div className="pb-hookTitle">Special hands and robot opponents</div>
      <p className="pb-hookText">
        Bid real deals from the world's top events, where the opponents make the same bids the real opponents made at the
        table. Or add robot opponents to random deals. Both partners need to be subscribers.
      </p>
      <Link className="pb-primary pb-hookBtn" to={SUBSCRIBE_URL}>Subscribe</Link>
    </div>
  );
}

// A Subscribe prompt next to the option a non-subscriber tapped.
function UpgradeNote({ what }) {
  return (
    <div className="pb-upgrade" role="status">
      <span>{what} for subscribers. Both you and your partner need a subscription.</span>
      <Link className="pb-primary pb-upgradeBtn" to={SUBSCRIBE_URL}>Subscribe</Link>
    </div>
  );
}

// ---------- Join from an invite link ----------
function JoinLink({ match, history }) {
  const [err, setErr] = useState("");
  useEffect(() => {
    pb("join", { code: match.params.code })
      .then((r) => history.replace(`/partner-bidding/${r.id}`))
      .catch((e) => setErr(e.message));
  }, [match.params.code, history]);
  return (
    <div className="pb-page">
      {err ? <p className="pb-err" role="alert">{err}</p> : <p className="pb-muted">Joining…</p>}
      {err && /members|subscribers/i.test(err) && <SubscribeHook />}
      <Link to="/partner-bidding">Your sets</Link>
    </div>
  );
}

// ---------- A set: its boards ----------
function SetView({ match, history }) {
  const { setId } = match.params;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => pb("set", { setId }).then(setData).catch((e) => setErr(e.message)), [setId]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, 15000, true);

  if (err) return <div className="pb-page"><p className="pb-err" role="alert">{err}</p><Link to="/partner-bidding">Your sets</Link></div>;
  if (!data) return <div className="pb-page"><p className="pb-muted">Loading…</p></div>;
  const { set, boards } = data;
  const left = boards.filter((b) => b.status !== "done").length;
  const leave = async () => {
    await pb("leave", { setId }).catch(() => {});
    history.push("/partner-bidding");
  };
  const nextYours = boards.find((b) => b.status === "your-turn");

  return (
    <div className="pb-page">
      <div className="pb-topbar">
        <Link to="/partner-bidding" className="pb-back pb-backText">‹ Your sets</Link>
        <span className="pb-starTotal pb-pillBox">★ {set.stars}</span>
        <span className="pb-pillBox">Deals left: {left}</span>
      </div>
      <h1 className="pb-title pb-title--sm">{set.partnerName ? `With ${set.partnerName}` : "Waiting for your partner"}</h1>
      {!set.partnerName && <p className="pb-muted">Your partner hasn't joined this set.</p>}
      {nextYours && (
        <Link className="pb-primary pb-nextBtn" to={`/partner-bidding/${setId}/${nextYours.n}`}>Bid board {nextYours.n} ›</Link>
      )}
      <ol className="pb-boards">
        {boards.map((b) => (
          <li key={b.n}>
            <Link to={`/partner-bidding/${setId}/${b.n}`} className={`pb-boardRow pb-boardRow--${b.status}`}>
              <span className="pb-boardNo">{b.n}</span>
              <HandLine hand={b.myHand} />
              <span className="pb-boardEnd">
                {b.status === "done" && (
                  <>
                    <ContractChip k={b.result.mine.contract} />
                    <span className="pb-decl">{b.result.mine.contract ? b.result.mine.contract.declarer : ""}</span>
                    <Stars n={b.result.stars} />
                  </>
                )}
                {b.status === "your-turn" && <span className="pb-pill pb-pill--you">Your turn</span>}
                {b.status === "partner-turn" && <span className="pb-pill">Partner's turn</span>}
                {b.status === "waiting-partner" && <span className="pb-muted">Waiting</span>}
                {b.status === "opponents" && <span className="pb-pill">Opponents</span>}
              </span>
            </Link>
          </li>
        ))}
      </ol>
      <button type="button" className="pb-linkBtn pb-leave" onClick={leave}>Remove this set from my list</button>
    </div>
  );
}

// ---------- A board ----------
function BoardView({ match, history }) {
  const { setId } = match.params;
  const n = Number(match.params.n);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => pb("board", { setId, n }).then((r) => { setData(r); setErr(""); }).catch((e) => setErr(e.message)), [setId, n]);
  useEffect(() => { setData(null); load(); }, [load]);
  const b = data && data.board;
  usePoll(load, 8000, !!b && (b.status === "partner-turn" || b.status === "opponents"));

  const onCall = async (c) => {
    setBusy(true);
    try {
      const r = await pb("call", { setId, n, call: c });
      setData((d) => ({ ...d, board: r.board }));
    } catch (e) {
      setErr(e.message);
      load();
    } finally {
      setBusy(false);
    }
  };
  const goNext = async () => {
    const r = await pb("set", { setId }).catch(() => null);
    const next = r && (r.boards.find((x) => x.status === "your-turn" && x.n > n) || r.boards.find((x) => x.status === "your-turn"));
    history.push(next ? `/partner-bidding/${setId}/${next.n}` : `/partner-bidding/${setId}`);
  };

  if (!b) return <div className="pb-page">{err ? <p className="pb-err" role="alert">{err}</p> : <p className="pb-muted">Loading…</p>}</div>;
  const seat = data.seat;
  const partnerSeat = seat === "S" ? "N" : "S";
  const partner = data.partnerName || "your partner";

  return (
    <div className="pb-page pb-page--board">
      <div className="pb-topbar">
        <Link to={`/partner-bidding/${setId}`} className="pb-back pb-backText">‹ All boards</Link>
        <div className="pb-boardTitle">
          <div>Board {n}</div>
          <div className="pb-muted">with {partner}</div>
        </div>
        <span className="pb-arrows">
          <button type="button" className="pb-arrow" disabled={n <= 1} onClick={() => history.push(`/partner-bidding/${setId}/${n - 1}`)} aria-label="Previous board">◀</button>
          <button type="button" className="pb-arrow" disabled={n >= data.size} onClick={() => history.push(`/partner-bidding/${setId}/${n + 1}`)} aria-label="Next board">▶</button>
        </span>
      </div>

      {b.status === "done" && b.partnerHand && <Fan hand={b.partnerHand} label={`${SEAT_NAME[partnerSeat]} ${b.partnerHcp} HCP`} />}

      <div className="pb-middle">
        <AuctionGrid auction={b.auction} notes={b.notes} dealer={b.dealer} vul={b.vul} toAct={b.status !== "done"} />
        {b.status === "done" && <ResultPanel result={b.result} dd={b.dd} />}
      </div>

      {b.status === "done" && b.featured && <TableRooms b={b} />}
      {b.status === "done" && b.hands && (
        <div className="pb-oppHands">
          {["W", "E"].map((s) => (
            <div key={s}>
              <span className="pb-fanLabel">{SEAT_NAME[s]}</span> <HandLine hand={b.hands[s]} />
            </div>
          ))}
        </div>
      )}

      {err && <p className="pb-err" role="alert">{err}</p>}
      {b.status === "your-turn" && <BidBox auction={b.auction} dealer={b.dealer} onCall={onCall} busy={busy} />}
      {b.status === "opponents" && <p className="pb-waiting">The opponents are bidding…</p>}
      {b.status === "partner-turn" && <p className="pb-waiting">Waiting for {partner} to bid. This page updates by itself.</p>}
      {b.status === "waiting-partner" && <p className="pb-waiting">Your partner hasn't joined this set yet.</p>}
      {(b.status === "done" || b.status === "partner-turn") && (
        <button type="button" className="pb-primary pb-nextBtn" onClick={goNext}>Next board to bid ›</button>
      )}

      <Fan hand={b.myHand} label={`${SEAT_NAME[seat]} ${b.myHcp} HCP`} />
    </div>
  );
}

function PartnerBidding(props) {
  const { match, uid, authReady } = props;
  const signedIn = !!uid || !!testUid();
  let body;
  if (!signedIn) {
    body = authReady ? (
      <div className="pb-page">
        <h1 className="pb-title">Partnership bidding</h1>
        <p>Please <Link to="/login">sign in</Link> to bid with a partner.</p>
      </div>
    ) : (
      <div className="pb-page"><p className="pb-muted">Loading…</p></div>
    );
  } else if (match.params.code) body = <JoinLink {...props} />;
  else if (match.params.n) body = <BoardView {...props} key={`${match.params.setId}-${match.params.n}`} />;
  else if (match.params.setId) body = <SetView {...props} key={match.params.setId} />;
  else body = <Home {...props} />;
  return (
    <div className="pb-root">
      <Helmet>
        <title>Partnership bidding | Bridge Champions</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {body}
    </div>
  );
}

export default connect((state) => ({
  uid: state.auth?.uid,
  authReady: state.auth?.authReady === true,
}))(PartnerBidding);
